// src/ventes/DetailBonLivraison.jsx
import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert, 
    Modal, Form, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 3000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailBonLivraison() {
    const navigate = useNavigate();
    const { id } = useParams();
    const printRef = useRef();

    const [bl, setBl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [showLivraisonModal, setShowLivraisonModal] = useState(false);
    const [showAnnulationModal, setShowAnnulationModal] = useState(false);
    const [showPreparationModal, setShowPreparationModal] = useState(false);

    const [livraisonInfo, setLivraisonInfo] = useState({
        date_livraison_reelle: new Date().toISOString().split('T')[0],
        commentaire: '',
        nom_livreur: '',
        signature: false
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });
    const showSuccess = (msg) => showToast('success', 'Succès', msg);
    const showError   = (msg) => showToast('error', 'Erreur', msg);
    const showInfo    = (msg) => showToast('info', 'Information', msg);

    useEffect(() => {
        fetchBonLivraison();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    /* ============================================================
       FETCH (logique conservée)
       ============================================================ */
    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setSocieteInfos(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    setLogoBase64(canvas.toDataURL('image/jpeg'));
                    resolve();
                };
                img.onerror = () => { setLogoBase64(''); resolve(); };
                img.src = logo;
            } catch (error) {
                setLogoBase64('');
                resolve();
            }
        });
    };

    const fetchBonLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setBl(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       ACTIONS (logique conservée)
       ============================================================ */
    const handleMarquerLivre = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/bons_livraison.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'marquer_livre',
                    id: id,
                    date_livraison_reelle: livraisonInfo.date_livraison_reelle,
                    nom_livreur: livraisonInfo.nom_livreur,
                    commentaire: livraisonInfo.commentaire
                })
            });
            const result = await response.json();
            if (result.success) {
                setShowLivraisonModal(false);
                fetchBonLivraison();
                showSuccess("Bon de livraison marqué comme livré");
            } else {
                showError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleAnnuler = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const getResponse = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!getResponse.ok) throw new Error("Erreur chargement");
            const getResult = await getResponse.json();
            const blData = getResult.data;

            const updateResponse = await fetch(`${API_BASE_URL}/bons_livraison.php?id=${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...blData, statut: 'annule' })
            });
            const updateResult = await updateResponse.json();
            if (updateResult.success) {
                setShowAnnulationModal(false);
                fetchBonLivraison();
                showSuccess("Bon de livraison annulé");
            } else {
                showError(updateResult.message || "Erreur lors de l'annulation");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handlePreparer = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            const getResponse = await fetch(
                `${API_BASE_URL}/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!getResponse.ok) throw new Error("Erreur chargement");
            const getResult = await getResponse.json();
            const blData = getResult.data;

            const updateResponse = await fetch(`${API_BASE_URL}/bons_livraison.php?id=${id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...blData, statut: 'prepare' })
            });
            const updateResult = await updateResponse.json();
            if (updateResult.success) {
                setShowPreparationModal(false);
                fetchBonLivraison();
                showSuccess("Bon de livraison marqué comme préparé");
            } else {
                showError(updateResult.message || "Erreur lors de la préparation");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    /* ============================================================
       IMPRESSION PROFESSIONNELLE — Design bordereau
       ============================================================ */
    const genererHTMLImpression = () => {
        if (!bl) return '';

        const statutConfig = {
            brouillon: { class: 'draft', label: 'BROUILLON' },
            prepare:   { class: 'prepared', label: 'PRÉPARÉ' },
            livre:     { class: 'delivered', label: 'LIVRÉ' },
            annule:    { class: 'cancelled', label: 'ANNULÉ' }
        };
        const statut = statutConfig[bl.statut] || statutConfig.brouillon;
        const totalProduits = bl?.lignes?.reduce((acc, l) => acc + (parseInt(l.quantite) || 0), 0) || 0;
        const logoSrc = logoBase64;
        const logoText = company.name.substring(0, 2).toUpperCase();

        return `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <title>Bon de livraison ${bl.numero_bl || ''}</title>
                <style>
                    @page { size: A4; margin: 12mm 10mm; }
                    * { margin: 0; padding: 0; box-sizing: border-box; }

                    body {
                        font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
                        background: white;
                        color: #1e293b;
                        font-size: 10.5px;
                        line-height: 1.5;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }

                    .invoice { max-width: 210mm; margin: 0 auto; background: white; position: relative; }

                    /* BANDEAU SUPÉRIEUR */
                    .top-band {
                        display: flex; justify-content: space-between; align-items: stretch;
                        border-radius: 8px 8px 0 0; overflow: hidden;
                        background: linear-gradient(135deg, #1a3c5e 0%, #295AA7 100%);
                        color: white;
                    }
                    .top-band-left {
                        padding: 22px 26px; display: flex; align-items: center; gap: 18px; flex: 1;
                    }
                    .logo-container {
                        width: 70px; height: 70px; background: white; border-radius: 8px;
                        display: flex; align-items: center; justify-content: center;
                        overflow: hidden; flex-shrink: 0;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    }
                    .logo-container img { max-width: 100%; max-height: 100%; object-fit: contain; }
                    .logo-fallback { font-size: 24px; font-weight: 800; color: #1a3c5e; letter-spacing: 1px; }
                    .company-header h1 {
                        font-size: 22px; font-weight: 800; letter-spacing: 2px;
                        margin-bottom: 4px; text-transform: uppercase;
                    }
                    .company-header .tagline {
                        font-size: 9px; letter-spacing: 3px; opacity: 0.75;
                        text-transform: uppercase; font-weight: 300;
                    }
                    .top-band-right {
                        padding: 22px 26px; text-align: right;
                        display: flex; flex-direction: column; justify-content: center;
                        min-width: 220px; background: #C42625;
                        border-left: 1px solid rgba(255,255,255,0.15);
                    }
                    .doc-type {
                        font-size: 22px; font-weight: 800; letter-spacing: 3px;
                        margin-bottom: 6px; text-transform: uppercase;
                    }
                    .doc-number {
                        font-size: 11px; font-family: 'Courier New', monospace;
                        font-weight: 600; letter-spacing: 1px; margin-bottom: 8px; opacity: 0.95;
                    }
                    .doc-date { font-size: 9px; opacity: 0.75; letter-spacing: 1px; }

                    /* STATUT */
                    .status-strip {
                        display: flex; justify-content: center; align-items: center;
                        padding: 8px 20px; font-size: 10px; font-weight: 700;
                        letter-spacing: 3px; text-transform: uppercase;
                        border-bottom: 3px solid;
                    }
                    .status-delivered { background: #ecfdf5; color: #065f46; border-color: #10b981; }
                    .status-prepared  { background: #eff6ff; color: #1e40af; border-color: #3b82f6; }
                    .status-draft     { background: #f1f5f9; color: #475569; border-color: #94a3b8; }
                    .status-cancelled { background: #fef2f2; color: #991b1b; border-color: #ef4444; }

                    /* PARTIES */
                    .parties {
                        display: flex; gap: 14px; padding: 20px 0;
                        border-bottom: 1px solid #e2e8f0;
                    }
                    .party {
                        flex: 1; padding: 14px 16px; background: #f8fafc;
                        border-radius: 8px; border-left: 3px solid #295AA7;
                    }
                    .party-client { border-left-color: #21ACCD; }
                    .party-label {
                        font-size: 8.5px; font-weight: 700; letter-spacing: 2px;
                        text-transform: uppercase; color: #295AA7;
                        margin-bottom: 8px; display: flex; align-items: center; gap: 6px;
                    }
                    .party-client .party-label { color: #21ACCD; }
                    .party-label::before {
                        content: ''; width: 6px; height: 6px;
                        border-radius: 50%; background: currentColor;
                    }
                    .party-name {
                        font-size: 13px; font-weight: 700;
                        color: #0f172a; margin-bottom: 6px;
                    }
                    .party-details { font-size: 9.5px; color: #475569; line-height: 1.7; }
                    .party-details .label {
                        display: inline-block; min-width: 70px;
                        color: #94a3b8; font-weight: 500;
                    }

                    /* MÉTADONNÉES */
                    .invoice-meta {
                        display: flex; justify-content: flex-end; gap: 10px;
                        padding: 14px 0; margin-bottom: 8px;
                    }
                    .meta-card {
                        min-width: 150px; padding: 8px 14px;
                        background: white; border: 1px solid #e2e8f0;
                        border-radius: 6px; text-align: center;
                    }
                    .meta-card .meta-label {
                        font-size: 8px; font-weight: 600; letter-spacing: 1.5px;
                        text-transform: uppercase; color: #94a3b8; margin-bottom: 3px;
                    }
                    .meta-card .meta-value {
                        font-size: 11px; font-weight: 700; color: #1e293b;
                    }
                    .meta-card.highlight {
                        background: linear-gradient(135deg, #eff6ff, #dbeafe);
                        border-color: #3b82f6;
                    }
                    .meta-card.highlight .meta-label { color: #1e40af; }
                    .meta-card.highlight .meta-value { color: #1e3a8a; }

                    /* NOTES */
                    .notes-box {
                        padding: 12px 16px; background: #fffbeb;
                        border-left: 3px solid #f59e0b;
                        border-radius: 6px; font-size: 9.5px;
                        color: #78350f; margin-bottom: 16px;
                    }
                    .notes-box strong { color: #92400e; }

                    /* TABLE */
                    .table-wrapper {
                        border-radius: 8px; overflow: hidden;
                        border: 1px solid #e2e8f0; margin-bottom: 16px;
                    }
                    table.lines { width: 100%; border-collapse: collapse; }
                    table.lines thead {
                        background: linear-gradient(135deg, #1a3c5e, #295AA7);
                        color: white;
                    }
                    table.lines thead th {
                        padding: 10px 12px; font-size: 9px;
                        font-weight: 700; letter-spacing: 1.2px;
                        text-transform: uppercase; text-align: left;
                    }
                    table.lines thead th.center { text-align: center; }
                    table.lines tbody td {
                        padding: 10px 12px; font-size: 10px;
                        color: #334155; border-bottom: 1px solid #f1f5f9;
                        vertical-align: top;
                    }
                    table.lines tbody tr:nth-child(even) { background: #fafbfc; }
                    table.lines tbody tr:last-child td { border-bottom: none; }
                    table.lines td.center { text-align: center; }
                    table.lines td.ref {
                        font-family: 'Courier New', monospace;
                        font-size: 9px; color: #64748b;
                    }
                    table.lines td.designation { font-weight: 600; color: #0f172a; }
                    table.lines td.quantity {
                        text-align: center; font-weight: 700; color: #1a3c5e;
                    }

                    /* TOTAUX */
                    .totals-section {
                        display: flex; justify-content: flex-end;
                        margin-bottom: 20px;
                    }
                    .totals-box {
                        width: 300px; flex-shrink: 0;
                        border-radius: 8px; overflow: hidden;
                        border: 1px solid #e2e8f0;
                    }
                    .totals-box .row {
                        display: flex; justify-content: space-between; align-items: center;
                        padding: 8px 14px; font-size: 10px;
                        border-bottom: 1px solid #f1f5f9;
                    }
                    .totals-box .row:last-child { border-bottom: none; }
                    .totals-box .row .lbl { color: #64748b; }
                    .totals-box .row .val { font-weight: 600; color: #334155; }
                    .totals-box .row.total {
                        background: linear-gradient(135deg, #1a3c5e, #295AA7);
                        color: white; padding: 12px 14px;
                    }
                    .totals-box .row.total .lbl {
                        color: white; font-weight: 700;
                        letter-spacing: 1.5px; font-size: 10px;
                        text-transform: uppercase;
                    }
                    .totals-box .row.total .val {
                        color: white; font-size: 15px; font-weight: 800;
                    }

                    /* SIGNATURES */
                    .signatures {
                        display: flex; justify-content: space-between; gap: 20px;
                        margin-top: 24px; padding-top: 18px;
                        border-top: 1px solid #e2e8f0;
                    }
                    .signature-box { flex: 1; text-align: center; padding-top: 8px; }
                    .signature-box .sig-title {
                        font-size: 9px; font-weight: 700; letter-spacing: 1.5px;
                        text-transform: uppercase; color: #64748b; margin-bottom: 4px;
                    }
                    .signature-box .sig-hint {
                        font-size: 8px; font-style: italic;
                        color: #94a3b8; margin-bottom: 20px;
                    }
                    .signature-box .sig-area {
                        height: 60px; border-bottom: 1.5px solid #cbd5e1;
                        margin-bottom: 6px;
                    }
                    .signature-box .sig-area.stamp {
                        border-bottom: 1.5px dashed #cbd5e1;
                    }

                    /* FOOTER */
                    .legal-footer {
                        margin-top: 22px; padding-top: 14px;
                        border-top: 2px solid #1a3c5e;
                        display: flex; justify-content: space-between; align-items: center;
                        font-size: 8px; color: #94a3b8; letter-spacing: 0.5px;
                    }
                    .legal-footer .legal-left { display: flex; gap: 16px; flex-wrap: wrap; }
                    .legal-footer .legal-right { text-align: right; font-weight: 600; color: #64748b; }
                    .legal-footer strong { color: #1a3c5e; }

                    /* WATERMARK */
                    .watermark {
                        position: fixed; top: 50%; left: 50%;
                        transform: translate(-50%, -50%) rotate(-35deg);
                        font-size: 100px; font-weight: 900; letter-spacing: 8px;
                        opacity: 0.05; pointer-events: none; z-index: 0;
                        white-space: nowrap; text-transform: uppercase;
                    }
                    .watermark.delivered { color: #10b981; }
                    .watermark.cancelled { color: #ef4444; }
                    .watermark.prepared  { color: #3b82f6; }

                    @media print {
                        body { background: white; padding: 0; }
                        .watermark { display: block; }
                    }
                </style>
            </head>
            <body>
                ${bl.statut === 'livre' ? '<div class="watermark delivered">LIVRÉ</div>' : ''}
                ${bl.statut === 'annule' ? '<div class="watermark cancelled">ANNULÉ</div>' : ''}
                ${bl.statut === 'prepare' ? '<div class="watermark prepared">PRÉPARÉ</div>' : ''}

                <div class="invoice">

                    <!-- BANDEAU SUPÉRIEUR -->
                    <div class="top-band">
                        <div class="top-band-left">
                            <div class="logo-container">
                                ${logoSrc ? `<img src="${logoSrc}" alt="Logo" />` : `<div class="logo-fallback">${logoText}</div>`}
                            </div>
                            <div class="company-header">
                                <h1>${company.name}</h1>
                                <div class="tagline">Bon de livraison</div>
                            </div>
                        </div>
                        <div class="top-band-right">
                            <div class="doc-type">Bon de livraison</div>
                            <div class="doc-number">N° ${bl.numero_bl || ''}</div>
                            <div class="doc-date">Émis le ${formatDate(bl.date_livraison)}</div>
                        </div>
                    </div>

                    <!-- BANDEAU STATUT -->
                    <div class="status-strip status-${statut.class}">
                        ${statut.label}
                    </div>

                    <!-- PARTIES -->
                    <div class="parties">
                        <div class="party">
                            <div class="party-label">Émetteur</div>
                            <div class="party-name">${company.name}</div>
                            <div class="party-details">
                                <div><span class="label">Adresse</span>${company.address}</div>
                                <div><span class="label">Téléphone</span>${company.phone}</div>
                                <div><span class="label">Email</span>${company.email}</div>
                                <div><span class="label">Site web</span>${company.site_web}</div>
                            </div>
                        </div>
                        <div class="party party-client">
                            <div class="party-label">Client / Destinataire</div>
                            <div class="party-name">${bl.client_nom || 'Client non spécifié'}</div>
                            <div class="party-details">
                                ${bl.code_client ? `<div><span class="label">Code</span>${bl.code_client}</div>` : ''}
                                ${bl.client_telephone ? `<div><span class="label">Téléphone</span>${bl.client_telephone}</div>` : ''}
                                ${bl.client_email ? `<div><span class="label">Email</span>${bl.client_email}</div>` : ''}
                                <div><span class="label">Livraison</span>${bl.adresse_livraison || bl.client_adresse || 'Non spécifiée'}</div>
                                ${bl.destinataire ? `<div><span class="label">Destinataire</span>${bl.destinataire}</div>` : ''}
                            </div>
                        </div>
                    </div>

                    <!-- MÉTADONNÉES -->
                    <div class="invoice-meta">
                        <div class="meta-card">
                            <div class="meta-label">Date de livraison</div>
                            <div class="meta-value">${formatDate(bl.date_livraison)}</div>
                        </div>
                        ${bl.numero_facture ? `
                            <div class="meta-card highlight">
                                <div class="meta-label">Facture associée</div>
                                <div class="meta-value">${bl.numero_facture}</div>
                            </div>
                        ` : ''}
                    </div>

                    ${bl.notes ? `
                        <div class="notes-box">
                            <strong>Note :</strong> ${bl.notes}
                        </div>
                    ` : ''}

                    ${bl.nom_livreur || bl.commentaire ? `
                        <div class="notes-box" style="background:#eff6ff; border-left-color:#3b82f6; color:#1e40af;">
                            ${bl.nom_livreur ? `<strong>Livreur :</strong> ${bl.nom_livreur}` : ''}
                            ${bl.commentaire ? `<div style="margin-top:4px;"><strong>Commentaire :</strong> ${bl.commentaire}</div>` : ''}
                        </div>
                    ` : ''}

                    <!-- TABLEAU DES LIGNES -->
                    <div class="table-wrapper">
                        <table class="lines">
                            <thead>
                                <tr>
                                    <th style="width: 15%;">Référence</th>
                                    <th style="width: 45%;">Désignation</th>
                                    <th class="center" style="width: 10%;">Qté</th>
                                    <th style="width: 15%;">Lot</th>
                                    <th style="width: 15%;">Péremption</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${bl?.lignes && bl.lignes.length > 0
                                    ? bl.lignes.map(ligne => `
                                        <tr>
                                            <td class="ref">${ligne.reference || '-'}</td>
                                            <td class="designation">${ligne.designation || ''}</td>
                                            <td class="quantity">${ligne.quantite || 0}</td>
                                            <td>${ligne.lot || '—'}</td>
                                            <td>${ligne.date_peremption ? formatDate(ligne.date_peremption) : '—'}</td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" style="text-align:center; padding:20px; color:#94a3b8;">Aucun produit dans ce bon de livraison</td></tr>'
                                }
                            </tbody>
                        </table>
                    </div>

                    <!-- TOTAUX -->
                    <div class="totals-section">
                        <div class="totals-box">
                            <div class="row">
                                <span class="lbl">Nombre de références</span>
                                <span class="val">${bl?.lignes?.length || 0}</span>
                            </div>
                            <div class="row total">
                                <span class="lbl">Quantité totale</span>
                                <span class="val">${totalProduits}</span>
                            </div>
                        </div>
                    </div>

                    <!-- SIGNATURES -->
                    <div class="signatures">
                        <div class="signature-box">
                            <div class="sig-title">Signature Transporteur</div>
                            <div class="sig-hint">Cachet et signature du livreur</div>
                            <div class="sig-area"></div>
                        </div>
                        <div class="signature-box">
                            <div class="sig-title">Signature Client</div>
                            <div class="sig-hint">Précédée de la mention « Reçu conforme »</div>
                            <div class="sig-area stamp"></div>
                        </div>
                    </div>

                    <!-- FOOTER LÉGAL -->
                    <div class="legal-footer">
                        <div class="legal-left">
                            ${company.rc ? `<span><strong>RC</strong> ${company.rc}</span>` : ''}
                            ${company.nif ? `<span><strong>NIF</strong> ${company.nif}</span>` : ''}
                            ${company.patente ? `<span><strong>Patente</strong> ${company.patente}</span>` : ''}
                        </div>
                        <div class="legal-right">
                            Document généré le ${new Date().toLocaleDateString('fr-FR')} — ${company.name}
                        </div>
                    </div>

                </div>
            </body>
            </html>
        `;
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLImpression();
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        showInfo("Document envoyé à l'impression");
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { gradient: 'linear-gradient(135deg, #64748b, #475569)', label: 'Brouillon', icon: 'ri-draft-line' },
            prepare:   { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', label: 'Préparé',   icon: 'ri-stack-line' },
            livre:     { gradient: 'linear-gradient(135deg, #10b981, #059669)', label: 'Livré',     icon: 'ri-check-double-line' },
            annule:    { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', label: 'Annulé',    icon: 'ri-close-circle-line' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <span className="status-badge-charte" style={{ background: config.gradient }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    const ActionButton = ({ icon, onClick, tooltip, disabled = false, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <Button
                onClick={onClick}
                disabled={disabled}
                className="action-btn-charte"
                style={{ '--btn-color': color || COLORS.primary }}
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    /* ============================================================
       LOADING / ERROR
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-truck-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement du bon de livraison
                        </h5>
                        <p className="text-muted small">Récupération des informations en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !bl) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Bon de livraison non trouvé"}</p>
                            </div>
                            <Button onClick={() => navigate('/ventes/blListe')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const totalProduits = bl.lignes?.reduce((acc, l) => acc + (parseInt(l.quantite) || 0), 0) || 0;

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }} ref={printRef}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-truck-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Détail du bon de livraison</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i>
                                        {bl.numero_bl}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i>
                                        {formatDate(bl.date_livraison)}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i>
                                        {bl.client_nom}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {getStatutBadge(bl.statut)}
                            <div className="d-flex gap-2">
                                <ActionButton icon="ri-arrow-left-line" onClick={() => navigate(-1)} tooltip="Retour" />
                                <ActionButton icon="ri-printer-line" onClick={handlePrint} tooltip="Imprimer" />
                                {bl.statut === 'brouillon' && (
                                    <>
                                        <ActionButton icon="ri-pencil-line" onClick={() => navigate(`/ventes/bl/modifier/${bl.id_bon_livraison}`)} tooltip="Modifier" color={COLORS.info} />
                                        <ActionButton icon="ri-stack-line" onClick={() => setShowPreparationModal(true)} tooltip="Préparer" color={COLORS.primary} />
                                    </>
                                )}
                                {bl.statut === 'prepare' && (
                                    <ActionButton icon="ri-check-double-line" onClick={() => setShowLivraisonModal(true)} tooltip="Marquer comme livré" color={COLORS.success} />
                                )}
                                {bl.statut !== 'livre' && bl.statut !== 'annule' && (
                                    <ActionButton icon="ri-close-circle-line" onClick={() => setShowAnnulationModal(true)} tooltip="Annuler" color={COLORS.danger} />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ===== CARTE RÉSUMÉ ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-barcode-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">N° BL</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary }}>{bl.numero_bl}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-calendar-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Date livraison</span>
                                            <div className="stat-value-charte">{formatDate(bl.date_livraison)}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                            <i className="ri-stack-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Articles</span>
                                            <div className="stat-value-charte">{bl.lignes?.length || 0} références</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="reste-box-charte" style={{
                                        background: `${COLORS.primary}10`,
                                        border: `1px solid ${COLORS.primary}25`
                                    }}>
                                        <span className="small text-muted d-block">Quantité totale</span>
                                        <span className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.4rem' }}>
                                            {totalProduits} unités
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== INFOS CLIENT & LIVRAISON ===== */}
                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-user-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations client</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">{bl.client_nom}</h5>
                                    <p className="info-line-charte"><i className="ri-barcode-line"></i>Code: <strong>{bl.code_client}</strong></p>
                                    {bl.client_telephone && <p className="info-line-charte"><i className="ri-phone-line"></i>{bl.client_telephone}</p>}
                                    {bl.client_email && <p className="info-line-charte"><i className="ri-mail-line"></i>{bl.client_email}</p>}
                                    {bl.client_adresse && <p className="info-line-charte"><i className="ri-map-pin-line"></i>{bl.client_adresse}</p>}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-truck-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations de livraison</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <div className="mini-info-box-charte">
                                                <span className="mini-info-label-charte">Destinataire</span>
                                                <span className="mini-info-value-charte">{bl.destinataire || bl.client_nom}</span>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="mini-info-box-charte">
                                                <span className="mini-info-label-charte">Facture associée</span>
                                                <span className="mini-info-value-charte">{bl.numero_facture || 'Aucune'}</span>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="mini-info-box-charte">
                                                <span className="mini-info-label-charte">Adresse de livraison</span>
                                                <span className="mini-info-value-charte">{bl.adresse_livraison || bl.client_adresse || 'Non spécifiée'}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== LIVREUR & NOTES ===== */}
                    {(bl.nom_livreur || bl.commentaire || bl.notes) && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="card-section-header">
                                <div className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                    <i className="ri-user-star-line"></i>
                                </div>
                                <h6 className="mb-0 fw-semibold">Livraison & Notes</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    {bl.nom_livreur && (
                                        <Col md={6}>
                                            <div className="mini-info-box-charte">
                                                <span className="mini-info-label-charte">Nom du livreur</span>
                                                <span className="mini-info-value-charte">{bl.nom_livreur}</span>
                                            </div>
                                        </Col>
                                    )}
                                    {bl.commentaire && (
                                        <Col md={6}>
                                            <div className="mini-info-box-charte">
                                                <span className="mini-info-label-charte">Commentaire</span>
                                                <span className="mini-info-value-charte">{bl.commentaire}</span>
                                            </div>
                                        </Col>
                                    )}
                                    {bl.notes && (
                                        <Col md={12}>
                                            <div className="notes-box-charte">
                                                <div className="notes-box-icon"><i className="ri-file-text-line"></i></div>
                                                <div>
                                                    <strong>Notes</strong>
                                                    <p className="mb-0 small">{bl.notes}</p>
                                                </div>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== TABLEAU PRODUITS ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Header className="card-section-header justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                                <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                    <i className="ri-shopping-cart-line"></i>
                                </div>
                                <h6 className="mb-0 fw-semibold">Produits à livrer</h6>
                            </div>
                            <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                <i className="ri-stack-line"></i>
                                Total: {totalProduits} unités
                            </span>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0 align-middle table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4" style={{ width: '15%' }}>Référence</th>
                                            <th style={{ width: '40%' }}>Désignation</th>
                                            <th className="text-center" style={{ width: '10%' }}>Qté</th>
                                            <th style={{ width: '15%' }}>Lot</th>
                                            <th className="pe-4" style={{ width: '20%' }}>Péremption</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bl.lignes?.map((ligne, index) => (
                                            <tr key={index}>
                                                <td className="ps-4">
                                                    <span className="reference-badge-charte">
                                                        <i className="ri-barcode-line"></i>{ligne.reference}
                                                    </span>
                                                </td>
                                                <td className="fw-semibold">{ligne.designation}</td>
                                                <td className="text-center">
                                                    <span className="quantity-badge-charte">{ligne.quantite}</span>
                                                </td>
                                                <td className="text-muted">{ligne.lot || '—'}</td>
                                                <td className="pe-4 text-muted">
                                                    {ligne.date_peremption ? formatDate(ligne.date_peremption) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL LIVRAISON ===== */}
            <Modal show={showLivraisonModal} onHide={() => setShowLivraisonModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-check-double-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Confirmer la livraison</Modal.Title>
                            <p className="text-white-50 mb-0 small">Marquer ce BL comme livré</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <Alert className="alert-info-charte mb-4">
                        <i className="ri-information-line me-2"></i>
                        Une fois confirmé, le bon de livraison sera marqué comme <strong>livré</strong>.
                    </Alert>

                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Date de livraison réelle</Form.Label>
                        <Form.Control
                            type="date"
                            value={livraisonInfo.date_livraison_reelle}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, date_livraison_reelle: e.target.value})}
                            className="input-charte"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Nom du livreur</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Nom du transporteur / livreur"
                            value={livraisonInfo.nom_livreur}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, nom_livreur: e.target.value})}
                            className="input-charte"
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="form-label-modern">Commentaire (optionnel)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={livraisonInfo.commentaire}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, commentaire: e.target.value})}
                            placeholder="Observations sur la livraison..."
                            className="input-charte"
                        />
                    </Form.Group>

                    <Form.Group className="mb-0">
                        <Form.Check
                            type="checkbox"
                            id="signature"
                            label="Je confirme que la livraison a été effectuée"
                            checked={livraisonInfo.signature}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, signature: e.target.checked})}
                            className="form-check-charte"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowLivraisonModal(false)} className="btn-modern-outline">Annuler</Button>
                    <Button onClick={handleMarquerLivre} disabled={saving || !livraisonInfo.signature} className="btn-modern-success">
                        {saving ? <><Spinner size="sm" className="me-2" />Confirmation...</> : <><i className="ri-check-double-line me-2"></i>Confirmer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL PRÉPARATION ===== */}
            <Modal show={showPreparationModal} onHide={() => setShowPreparationModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-stack-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Préparer le bon</Modal.Title>
                            <p className="text-white-50 mb-0 small">Confirmer la préparation</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <p className="text-muted mb-4">
                        Confirmez-vous la préparation de ce bon de livraison ?
                    </p>
                    <Alert className="alert-info-charte text-start mb-0">
                        <div className="d-flex align-items-start gap-3">
                            <i className="ri-information-line fs-4"></i>
                            <div>
                                <small className="d-block">✓ Les produits seront déduits du stock</small>
                                <small className="d-block">✓ Le BL sera marqué comme « Préparé »</small>
                            </div>
                        </div>
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowPreparationModal(false)} className="btn-modern-outline">Annuler</Button>
                    <Button onClick={handlePreparer} disabled={saving} className="btn-modern-primary">
                        {saving ? <><Spinner size="sm" className="me-2" />Préparation...</> : <><i className="ri-stack-line me-2"></i>Confirmer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== MODAL ANNULATION ===== */}
            <Modal show={showAnnulationModal} onHide={() => setShowAnnulationModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon"><i className="ri-close-circle-line"></i></div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Annuler le bon</Modal.Title>
                            <p className="text-white-50 mb-0 small">Cette action est irréversible</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <p className="text-muted mb-4">
                        Êtes-vous sûr de vouloir annuler ce bon de livraison ?
                    </p>
                    <Alert className="alert-error-charte mb-0">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-alert-line"></i></div>
                            <div className="ms-3">
                                <strong>Attention</strong>
                                <p className="mb-0 small">Cette action est irréversible</p>
                            </div>
                        </div>
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button onClick={() => setShowAnnulationModal(false)} className="btn-modern-outline">Retour</Button>
                    <Button onClick={handleAnnuler} disabled={saving} className="btn-modern-danger">
                        {saving ? <><Spinner size="sm" className="me-2" />Annulation...</> : <><i className="ri-close-circle-line me-2"></i>Confirmer</>}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .status-badge-charte {
                    color: white; padding: 0.4rem 1.2rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.5rem;
                }

                /* ===== ACTION BUTTON ===== */
                .action-btn-charte {
                    width: 42px; height: 42px; border-radius: 12px;
                    display: inline-flex; align-items: center; justify-content: center;
                    border: 1.5px solid #e2e8f0; background: white;
                    color: #64748b; font-size: 1.1rem;
                    transition: all 0.2s ease;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== STAT ITEM ===== */
                .stat-item-charte { display: flex; align-items: center; gap: 0.75rem; }
                .stat-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .stat-label-charte {
                    display: block; font-size: 0.7rem; font-weight: 500;
                    text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
                }
                .stat-value-charte { font-size: 1.05rem; font-weight: 600; color: #1e293b; }
                .reste-box-charte {
                    padding: 0.75rem 1.25rem; border-radius: 12px;
                    text-align: right; display: inline-block;
                }

                /* ===== SECTION HEADER ===== */
                .card-section-header {
                    background: transparent; border: none;
                    padding: 1.25rem 1.5rem 0.75rem;
                    display: flex; align-items: center; gap: 0.75rem;
                }
                .section-icon-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                }
                .card-hover-charte { transition: all 0.3s ease; }
                .card-hover-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 32px rgba(0,0,0,0.08) !important;
                }

                /* ===== INFO LINE ===== */
                .info-line-charte {
                    display: flex; align-items: center; gap: 0.6rem;
                    font-size: 0.85rem; color: #475569; margin-bottom: 0.6rem;
                }
                .info-line-charte i { color: var(--secondary); font-size: 1rem; }

                /* ===== MINI INFO BOX ===== */
                .mini-info-box-charte {
                    background: #f8fafc; border-radius: 10px;
                    padding: 0.75rem 1rem;
                    border: 1px solid #f1f5f9;
                }
                .mini-info-label-charte {
                    display: block; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.4px;
                    color: #94a3b8; font-weight: 600; margin-bottom: 4px;
                }
                .mini-info-value-charte {
                    font-size: 0.85rem; font-weight: 600; color: #1e293b;
                }

                /* ===== NOTES BOX ===== */
                .notes-box-charte {
                    display: flex; align-items: flex-start; gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #fffbeb;
                    border-left: 4px solid var(--warning);
                    border-radius: 10px;
                }
                .notes-box-icon {
                    width: 36px; height: 36px; border-radius: 10px;
                    background: rgba(245,158,11,0.15);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--warning); font-size: 1.1rem;
                    flex-shrink: 0;
                }
                .notes-box-charte strong { color: #92400e; font-size: 0.85rem; }
                .notes-box-charte p { color: #78350f; }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    background: ${COLORS.primary}15; color: var(--primary);
                    padding: 0.25rem 0.75rem; border-radius: 50px;
                    font-size: 0.8rem; font-weight: 700;
                    min-width: 40px;
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-charte {
                    border: 2px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 0.6rem 0.9rem;
                    font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-charte:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .form-check-charte .form-check-input:checked {
                    background-color: var(--secondary);
                    border-color: var(--secondary);
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary, .btn-modern-success, .btn-modern-danger {
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-danger {
                    background: linear-gradient(135deg, var(--danger), #dc2626);
                    box-shadow: 0 4px 12px rgba(239,68,68,0.3);
                }
                .btn-modern-danger:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white;
                }
                .btn-modern-primary:disabled, .btn-modern-success:disabled, .btn-modern-danger:disabled {
                    opacity: 0.6; transform: none;
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }
                .alert-info-charte {
                    border: none; background: #eff6ff;
                    border-left: 4px solid var(--info);
                    border-radius: 12px; padding: 1rem 1.25rem;
                    font-size: 0.85rem; color: #1e40af;
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0; border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .reste-box-charte { width: 100%; text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}